import type {
    AddCommerceCartLineRequest,
    CommerceCart,
    CommerceOperationOptions,
    UpdateCommerceCartLineQuantityRequest,
} from "@teqtank/commerce-contracts";
import { CommerceRepository } from "../repositories/commerce-repository.js";
import { addCommerceCartLineOperation } from "../operations/add-commerce-cart-line-operation.js";
import { applyCommerceCartCouponOperation } from "../operations/apply-commerce-cart-coupon-operation.js";
import { clearCommerceCartOperation } from "../operations/clear-commerce-cart-operation.js";
import { getCommerceCartOperation } from "../operations/get-commerce-cart-operation.js";
import { removeCommerceCartCouponOperation } from "../operations/remove-commerce-cart-coupon-operation.js";
import { removeCommerceCartLineOperation } from "../operations/remove-commerce-cart-line-operation.js";
import { updateCommerceCartLineQuantityOperation } from "../operations/update-commerce-cart-line-quantity-operation.js";
import { resolveCommerceCart } from "./cart-response-resolver.js";

export class CommerceCartRepository extends CommerceRepository {
    public async get(options: CommerceOperationOptions): Promise<CommerceCart> {
        return resolveCommerceCart(
            (
                await this.execute(
                    getCommerceCartOperation,
                    {},
                    options,
                    "cart-operation-failed",
                    "Teqtank Commerce could not load the Cart.",
                )
            ).cart,
        );
    }
    public async add(
        request: AddCommerceCartLineRequest,
        options: CommerceOperationOptions,
    ): Promise<CommerceCart> {
        return resolveCommerceCart(
            (
                await this.execute(
                    addCommerceCartLineOperation,
                    request,
                    options,
                    "cart-operation-failed",
                    "Teqtank Commerce could not add the Product to the Cart.",
                    { productId: request.productId },
                )
            ).cart,
        );
    }
    public async updateQuantity(
        request: UpdateCommerceCartLineQuantityRequest,
        options: CommerceOperationOptions,
    ): Promise<CommerceCart> {
        return resolveCommerceCart(
            (
                await this.execute(
                    updateCommerceCartLineQuantityOperation,
                    request,
                    options,
                    "cart-operation-failed",
                    "Teqtank Commerce could not update the Cart line.",
                    { lineId: request.lineId },
                )
            ).cart,
        );
    }
    public async remove(
        lineId: string,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCart> {
        const request = {
            lineId,
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        };
        return resolveCommerceCart(
            (
                await this.execute(
                    removeCommerceCartLineOperation,
                    request,
                    options,
                    "cart-operation-failed",
                    "Teqtank Commerce could not remove the Cart line.",
                    { lineId },
                )
            ).cart,
        );
    }
    public async clear(
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCart> {
        const request = expectedVersion === undefined ? {} : { expectedVersion };
        return resolveCommerceCart(
            (
                await this.execute(
                    clearCommerceCartOperation,
                    request,
                    options,
                    "cart-operation-failed",
                    "Teqtank Commerce could not clear the Cart.",
                )
            ).cart,
        );
    }
    public async applyCoupon(
        code: string,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCart> {
        const request = {
            code,
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        };
        return resolveCommerceCart(
            (
                await this.execute(
                    applyCommerceCartCouponOperation,
                    request,
                    options,
                    "cart-operation-failed",
                    `Teqtank Commerce could not apply Coupon '${code}'.`,
                    { code },
                )
            ).cart,
        );
    }
    public async removeCoupon(
        code: string,
        expectedVersion: number | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCart> {
        const request = {
            code,
            ...(expectedVersion !== undefined ? { expectedVersion } : {}),
        };
        return resolveCommerceCart(
            (
                await this.execute(
                    removeCommerceCartCouponOperation,
                    request,
                    options,
                    "cart-operation-failed",
                    `Teqtank Commerce could not remove Coupon '${code}'.`,
                    { code },
                )
            ).cart,
        );
    }
}
